import { listen, safeImageUrl, setHidden, toText } from './dom.js';

const FOCUSABLE = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function focusableElements(modal) {
  return [...modal.querySelectorAll(FOCUSABLE)].filter(node => !node.hidden);
}

function normalizedPreview(result, fallback) {
  if (result instanceof Blob) return { ...fallback, blob: result };
  if (typeof result === 'string') return { ...fallback, fileUrl: result };
  if (result && typeof result === 'object') {
    return {
      ...fallback,
      ...result,
      fileUrl: result.fileUrl || result.src || fallback.fileUrl
    };
  }
  return fallback;
}

export function createDialogController({ imageModal, exportModal, onExportSubmit }) {
  const imageCard = imageModal.querySelector('[role="dialog"]');
  const exportCard = exportModal.querySelector('[role="dialog"]');
  const imageTitle = imageModal.querySelector('#imageModalTitle');
  const imageDescription = imageModal.querySelector('#imageModalDescription');
  const image = imageModal.querySelector('#imageModalAsset');
  const exportForm = exportModal.querySelector('#exportForm');
  const exportStatus = exportModal.querySelector('#exportStatus');
  const confirmExport = exportModal.querySelector('#confirmExportButton');
  const removers = [];
  let activeModal = null;
  let previousFocus = null;
  let objectUrl = '';

  function revokeObjectUrl() {
    if (!objectUrl) return;
    URL.revokeObjectURL(objectUrl);
    objectUrl = '';
  }

  function clearImageSource() {
    image.removeAttribute('src');
    image.hidden = true;
  }

  function open(modal, preferredFocus) {
    if (activeModal && activeModal !== modal) close(activeModal, false);
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    activeModal = modal;
    setHidden(modal, false);
    queueMicrotask(() => {
      const target = preferredFocus || focusableElements(modal)[0] || modal.querySelector('[role="dialog"]');
      target?.focus();
    });
  }

  function close(modal = activeModal, restoreFocus = true) {
    if (!modal) return;
    setHidden(modal, true);
    if (modal === imageModal) {
      clearImageSource();
      revokeObjectUrl();
    }
    if (modal === activeModal) activeModal = null;
    if (restoreFocus && previousFocus?.isConnected) previousFocus.focus();
    previousFocus = null;
  }

  function updateImage(result, fallbackItem = {}) {
    const preview = normalizedPreview(result, fallbackItem);
    clearImageSource();
    revokeObjectUrl();
    let source = '';
    if (preview.blob instanceof Blob) {
      objectUrl = URL.createObjectURL(preview.blob);
      source = objectUrl;
    } else {
      source = safeImageUrl(preview.fileUrl);
    }
    imageTitle.textContent = toText(preview.title, '图片预览');
    imageDescription.textContent = toText(preview.caption || preview.note, '当前图片没有补充说明。');
    image.alt = toText(preview.title, '项目成果图');
    if (source) {
      image.src = source;
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.hidden = true;
      imageDescription.textContent = '当前图片资源暂不可用，请检查资源文件后重试。';
    }
  }

  function openImage(item) {
    updateImage(item, item);
    open(imageModal, imageCard?.querySelector('[data-dialog-close]'));
  }

  function setExportStatus(message, type = 'info') {
    exportStatus.textContent = toText(message);
    exportStatus.dataset.status = type;
  }

  function setExportBusy(busy) {
    confirmExport.disabled = Boolean(busy);
    confirmExport.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function openExport() {
    setExportStatus('');
    setExportBusy(false);
    const checked = exportForm.querySelector('input[name="exportFormat"]:checked');
    open(exportModal, checked || exportCard);
  }

  function selectedFormat() {
    return exportForm.querySelector('input[name="exportFormat"]:checked')?.value || 'pptx';
  }

  async function submitExport(event) {
    event.preventDefault();
    setExportBusy(true);
    try {
      await onExportSubmit?.({
        format: selectedFormat(),
        setStatus: setExportStatus,
        close: () => close(exportModal)
      });
    } finally {
      setExportBusy(false);
    }
  }

  function trapFocus(event) {
    if (!activeModal) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close(activeModal);
      return;
    }
    if (event.key !== 'Tab') return;
    const rows = focusableElements(activeModal);
    if (!rows.length) return;
    const first = rows[0];
    const last = rows.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  for (const modal of [imageModal, exportModal]) {
    removers.push(listen(modal, 'click', event => {
      if (event.target === modal) close(modal);
    }));
    for (const button of modal.querySelectorAll('[data-dialog-close]')) {
      removers.push(listen(button, 'click', () => close(modal)));
    }
  }
  removers.push(listen(image, 'error', () => {
    image.hidden = true;
    imageDescription.textContent = '图片加载失败。已有成果数据未被修改，可关闭预览后重试。';
  }));
  removers.push(listen(exportForm, 'submit', submitExport));
  removers.push(listen(document, 'keydown', trapFocus));

  return {
    openImage,
    updateImage,
    openExport,
    close,
    setExportStatus,
    setExportBusy,
    getExportFormat: selectedFormat,
    destroy() {
      if (activeModal) close(activeModal, false);
      clearImageSource();
      revokeObjectUrl();
      removers.splice(0).forEach(remove => remove());
    }
  };
}
